import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const normalizeUser = (user = {}) => ({
  ...user,
  id: Number(getSalesId(user) || 0),
  fullName: String(
    getSalesValue(user, 'fullName', 'FullName', 'name', 'Name') ||
      `${getSalesValue(user, 'firstName', 'FirstName') || ''} ${getSalesValue(user, 'lastName', 'LastName') || ''}`.trim() ||
      getSalesValue(user, 'email', 'Email') ||
      ''
  ),
  email: String(getSalesValue(user, 'email', 'Email') || ''),
  phoneNumber: String(getSalesValue(user, 'phoneNumber', 'PhoneNumber', 'phone', 'Phone') || ''),
  rolesString: String(
    getSalesValue(user, 'rolesString', 'RolesString', 'roleNames', 'RoleNames') ||
      (Array.isArray(getSalesValue(user, 'roles', 'Roles'))
        ? getSalesValue(user, 'roles', 'Roles').map((role) => role?.name || role?.Name || role).join(', ')
        : '')
  ),
  isActive: Boolean(getSalesValue(user, 'isActive', 'IsActive') ?? true),
});

const normalizeUsers = (rows) => rows.map(normalizeUser);

const UserList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionId, setActionId] = useState(null);

  const {
    items: users,
    setItems: setUsers,
    loading,
    error,
    request,
    endpoints,
    reload,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.users.base, {
    normalize: normalizeUsers,
    errorMessage: 'Unable to load users.',
  });

  const filteredUsers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) =>
      [user.fullName, user.email, user.phoneNumber, user.rolesString]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [searchTerm, users]);

  const toggleActive = async (user) => {
    if (!canEditSales || !user.id) return;
    const nextUser = { ...user, isActive: !user.isActive, IsActive: !user.isActive };
    setActionError('');
    setActionId(user.id);

    try {
      await request({ method: 'PUT', url: endpoints.users.byId(user.id), data: nextUser });
      setUsers((current) => current.map((item) => (item.id === user.id ? normalizeUser(nextUser) : item)));
    } catch {
      setActionError('Unable to update user status.');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (user) => {
    if (!canEditSales || !user.id) return;
    if (!window.confirm(`Delete ${user.fullName || user.email || 'this user'}?`)) return;
    setActionError('');
    setActionId(user.id);

    try {
      await request({ method: 'DELETE', url: endpoints.users.byId(user.id) });
      await reload();
    } catch {
      setActionError('Unable to delete user.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-users-title">
      <Row className="mb-3">
        <Col><h4 id="sales-users-title">User List</h4></Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Button className="btn-success addbtnview" disabled={!canEditSales}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New User
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="danger">{actionError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Roles</th>
                  <th>Active</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading users...</div></td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={6}><div className="sales-no-data">No Data Available</div></td></tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id || user.email} className="table-border">
                      <td>{user.fullName}</td>
                      <td>{user.email}</td>
                      <td>{user.phoneNumber}</td>
                      <td>{user.rolesString}</td>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={user.isActive}
                          disabled={!canEditSales || actionId === user.id}
                          onChange={() => toggleActive(user)}
                        />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" className="edit-icon" title="Edit" disabled={!canEditSales}>
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button type="button" className="edit-icon" title="Delete" disabled={!canEditSales || actionId === user.id} onClick={() => handleDelete(user)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UserList;
